import { useCallback, useEffect, useState } from 'react';
import { Card, EmptyState, Progress, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { labelFor } from '@/shared/lib/labels';
import { agentApi, type AgentQuickAction } from '@/shared/api/agentApi';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';
import { PredictiveStandby } from './PredictiveStandby';
import { QuickActionsRow } from '../components/QuickActionsRow';
import { CallControlBar } from '../components/CallControlBar';

interface Campaign {
  id: number;
  name: string;
  status: string;
  dialer_mode: string;
  in_window?: boolean;
  window_today?: { open: string; close: string } | null;
  lines_per_agent?: number;
  quick_actions?: AgentQuickAction[];
  wrapup_required?: boolean;
  wrapup_blocks_next?: boolean;
  auto_dial_next?: boolean;
  transfer_allowed?: boolean;
}
interface Lead { id: number; phone: string; name?: string; fields?: Record<string, unknown> }
interface Disposition { code: string; label: string; requires_followup?: boolean; requires_note?: boolean; lead_status?: string | null }
/** מוצר לבחירה בעסקה — שם בלבד, בלי מחיר (אפיון 6.9). */
interface ProductOption { id: number; name: string }
interface ProgressData { total: number; remaining: number; done: number }

/** אותה הגדרה כמו ProductCatalog::isDealDisposition בשרת. */
function isDealCode(def: Disposition | undefined, code: string): boolean {
  return def?.lead_status === 'deal_closed' || (def?.code ?? code) === 'deal';
}

const FALLBACK_DISPOSITIONS: Disposition[] = [
  { code: 'deal', label: 'עסקה' },
  { code: 'follow_up', label: 'מעקב', requires_followup: true },
  { code: 'not_interested', label: 'לא מעוניין' },
  { code: 'no_answer', label: 'לא ענה' },
  { code: 'callback', label: 'חזרה', requires_followup: true },
  { code: 'dnc', label: 'הסר מרשימה' },
];

type Phase = 'lead' | 'wrapup';

export function Dialer() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [phase, setPhase] = useState<Phase>('lead');
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [dispositions, setDispositions] = useState<Disposition[]>(FALLBACK_DISPOSITIONS);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productId, setProductId] = useState('');
  const [chosen, setChosen] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [notes, setNotes] = useState('');
  // Set once a disposition has been submitted for the current lead — gates the
  // "next lead" step when the campaign requires wrap-up before continuing.
  const [disposed, setDisposed] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [batchDialing, setBatchDialing] = useState(false);
  // Default flow: starting a campaign fires a parallel batch for THIS agent
  // (first answer is bridged to them, the rest are dropped). Manual mode is
  // the old one-lead-at-a-time preview flow.
  const [manualMode, setManualMode] = useState(false);
  const [lastBatch, setLastBatch] = useState<{ dialed: number; leads: { phone: string; name?: string | null }[] } | null>(null);
  const dial = useSipStore((s) => s.dial);
  const sipState = useSipStore((s) => s.state);
  const activeCall = useSipStore((s) => s.active);
  const registered = useSipStore((s) => s.registered);

  useEffect(() => {
    void agentApi.dialerCampaigns().then((d) => setCampaigns(d as Campaign[])).catch(() => setCampaigns([]));
    void agentApi.callDispositions().then((d) => { if (d.length) setDispositions(d as Disposition[]); }).catch((e) => console.warn('[dispositions] load failed, using fallback', e));
  }, []);

  // Reload dispositions scoped to the active campaign so per-campaign rules
  // (e.g. requires_note) are enforced for this campaign's codes.
  useEffect(() => {
    if (!activeCampaign) return;
    void agentApi.callDispositions(activeCampaign.id).then((d) => { if (d.length) setDispositions(d as Disposition[]); }).catch(() => {});
    // מוצרי הקמפיין לבחירה בעסקה. כישלון אינו חוסם תיעוד — הבורר פשוט
    // לא יוצג, והשרת מקבל עסקה בלי מוצר.
    void agentApi.agentProducts(activeCampaign.id).then(setProducts).catch(() => setProducts([]));
  }, [activeCampaign]);

  const loadProgress = useCallback((id: number) => {
    void agentApi.dialerProgress(id).then((p) => setProgress(p as ProgressData)).catch(() => {});
  }, []);

  async function startCampaign(campaign: Campaign) {
    setActiveCampaign(campaign);
    setExhausted(false);
    setManualMode(false);
    setLastBatch(null);
    // Predictive campaigns are server-driven — the agent waits in standby and
    // the server dials/bridges. Don't pull a lead to dial manually here.
    if (campaign.dialer_mode === 'predictive') return;
    loadProgress(campaign.id);
    // Starting a campaign = fire the first parallel batch for this agent.
    await dialBatch(campaign);
  }

  async function fetchNext(campaign: Campaign) {
    try {
      const l = (await agentApi.dialerNext(campaign.id)) as Lead | null;
      setFetchError(false);
      if (!l) {
        setExhausted(true);
        setLead(null);
      } else {
        setLead(l);
        setPhase('lead');
        setChosen('');
        setCallbackAt('');
        setNotes('');
        setDisposed(false);
      }
      loadProgress(campaign.id);
    } catch {
      // Don't dead-end mid-campaign on "טוען ליד…" — show a retryable error.
      setFetchError(true);
      toast('שגיאה בטעינת הליד הבא', 'danger');
    }
  }

  async function dialLead() {
    if (!lead) return;
    // בלי רישום SIP אין קו — dial() של הסטור לא זורק, רק נכנס למצב שגיאה,
    // והנציג היה מגיע ל-wrap-up של שיחה שלא התקיימה.
    // ניסיון חיבור מחדש לפני שמוותרים: קו שנפל וחזר לא נרשם מעצמו.
    if (!registered && !(await useSipStore.getState().reconnect())) {
      toast('הסופטפון לא מחובר — בדוק את החיבור או רענן את הדף לפני חיוג', 'danger');
      return;
    }
    try {
      // JsSIP only — calling agentApi.dial() as well originates a second
      // ARI leg to the same lead (rings twice).
      await dial(lead.phone);
      setPhase('wrapup');
    } catch {
      toast('שגיאה בחיוג', 'danger');
    }
  }

  async function saveDisposition() {
    if (!lead || !activeCampaign || !chosen) {
      toast('יש לבחור קוד סיום', 'warn');
      return;
    }
    const def = dispositions.find((d) => d.code === chosen);
    // A follow-up disposition without a scheduled time would silently mark the
    // lead "completed" and lose it — require the callback time first.
    if (def?.requires_followup && !callbackAt) {
      toast('קוד זה דורש מועד חזרה — בחר תאריך ושעה', 'warn');
      return;
    }
    // Same mandatory-note rule as the softphone wrap-up — enforced here too so
    // it can't be dodged via the dialer's light wrap-up.
    if (def?.requires_note && !notes.trim()) {
      toast('סטטוס זה מחייב הערה', 'warn');
      return;
    }
    const nextStatus = chosen === 'dnc' ? 'dnc' : def?.requires_followup ? 'queued' : 'completed';
    try {
      await agentApi.dialerDispose(
        lead.id, chosen, callbackAt || undefined, nextStatus, notes.trim() || undefined,
        { product_id: isDealCode(def, chosen) && productId ? Number(productId) : null },
      );
      toast('נשמר', 'ok');
      setDisposed(true);
      setProductId('');
      // auto_dial_next (default): advance to the next lead immediately. When the
      // campaign turns it off, wait for the agent's explicit "next lead" click.
      if (activeCampaign.auto_dial_next !== false) {
        await fetchNext(activeCampaign);
      }
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשמירה', 'danger');
    }
  }

  async function skip() {
    if (!lead || !activeCampaign) return;
    try {
      await agentApi.dialerDispose(lead.id, 'skipped', undefined, 'queued');
    } catch {
      toast('שגיאה בדילוג על הליד', 'danger');
      return;
    }
    await fetchNext(activeCampaign);
  }

  // Batch dial: the server fires the campaign's parallel lines at once; the
  // first customer to answer is bridged to THIS agent's softphone and the rest
  // are hung up + requeued. The bridged call arrives as an incoming call.
  async function dialBatch(campaign?: Campaign) {
    const target = campaign ?? activeCampaign;
    if (!target || batchDialing) return;
    setBatchDialing(true);
    try {
      // השיחה המגושרת מצלצלת בסופטפון — חייב רישום. register() בלי קרדנצ'לס
      // הוא no-op שדורס את הרישום האמיתי (הרישום מנוהל גלובלית ב-AgentLayout).
      if (!registered && !(await useSipStore.getState().reconnect())) {
        toast('הסופטפון לא מחובר — בדוק את החיבור או רענן את הדף לפני סבב חיוג', 'danger');
        return;
      }
      const res = await agentApi.dialerDialBatch(target.id);
      setLastBatch({ dialed: res?.dialed ?? 0, leads: (res as { leads?: { phone: string; name?: string | null }[] })?.leads ?? [] });
      toast(`מחייג ${res?.dialed ?? ''} מספרים במקביל — הראשון שעונה יחובר אליך והשאר ינותקו`, 'ok');
      loadProgress(target.id);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בחיוג המקבילי', 'danger');
    } finally {
      setBatchDialing(false);
    }
  }

  async function switchToManual() {
    if (!activeCampaign) return;
    setManualMode(true);
    setLastBatch(null);
    await fetchNext(activeCampaign);
  }

  function exit() {
    setActiveCampaign(null);
    setLead(null);
    setProgress(null);
    setExhausted(false);
  }

  if (!campaigns.length) {
    return <EmptyState icon="target" title="אין קמפיינים פעילים" description="המתן עד שמנהל יוסיף אותך לקמפיין." />;
  }

  // Campaign picker
  if (!activeCampaign) {
    return (
      <div className="stack" style={{ padding: 'var(--gap)' }}>
        <PageHeader title="קמפיינים יוצאים" />
        {campaigns.map((c) => {
          const outside = c.in_window === false;
          return (
            <Card key={c.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div>
                  <strong>{c.name}</strong>
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{labelFor('dialer_mode', c.dialer_mode)}</div>
                  {outside && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4, fontSize: 'var(--t-xs)', color: 'var(--warn, #b45309)' }}>
                      <Icon name="clock" size={13} />
                      מחוץ לשעות החיוג
                      {c.window_today
                        ? ` — חיוג יתאפשר בין ${c.window_today.open} ל-${c.window_today.close}`
                        : ' — אין חיוג היום'}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn primary"
                  title={outside ? 'מחוץ לשעות הפעילות — תוצג הודעה והחיוג ימשיך' : undefined}
                  onClick={() => startCampaign(c)}
                >
                  התחל
                </button>
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  // Predictive campaigns use the server-driven standby screen, not the manual
  // one-lead-at-a-time preview flow below.
  if (activeCampaign.dialer_mode === 'predictive') {
    return <PredictiveStandby campaign={activeCampaign} onExit={exit} />;
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)', maxWidth: 560 }}>
      <PageHeader title={activeCampaign.name} actions={<>
        {manualMode && (
          <button type="button" className="btn" onClick={() => void dialBatch()} disabled={batchDialing} title="השרת מחייג לכמה לקוחות בו-זמנית; הראשון שעונה מחובר אליך והשאר מנותקים">
            <Icon name="bolt" size={14} /> {batchDialing ? 'מחייג…' : `חיוג מקבילי (${activeCampaign.lines_per_agent ?? 3} קווים)`}
          </button>
        )}
        <button type="button" className="btn ghost" onClick={exit}>← חזרה לקמפיינים</button>
      </>} />

      {activeCall?.callId && sipState === 'on-call' && activeCampaign.transfer_allowed && (
        <Card>
          <CallControlBar callId={activeCall.callId} transferAllowed={activeCampaign.transfer_allowed} />
        </Card>
      )}

      {progress && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--t-sm)', marginBottom: 6 }}>
            <span>טופלו {progress.done} מתוך {progress.total}</span>
            <span style={{ color: 'var(--muted)' }}>נותרו {progress.remaining}</span>
          </div>
          <Progress value={progress.done} max={progress.total || 1} />
        </Card>
      )}

      {!manualMode ? (
        <Card title={`חיוג מקבילי — ${activeCampaign.lines_per_agent ?? 3} קווים`}>
          <div className="stack">
            <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              השרת מחייג לכמה לקוחות בו-זמנית עבורך. הלקוח הראשון שעונה מחובר אליך לסופטפון,
              וכל שאר הקווים מנותקים אוטומטית והלידים שלהם חוזרים לתור.
            </p>
            {lastBatch && (
              <div style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)' }}>
                <strong>{sipState === 'on-call' ? 'שיחה פעילה' : `חייגתי ל-${lastBatch.dialed} מספרים — ממתין למענה…`}</strong>
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {lastBatch.leads.map((l, i) => (
                    <span key={i} dir="ltr" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-pill)', padding: '2px 10px', fontSize: 'var(--t-xs)' }}>
                      {l.name ? `${l.name} · ` : ''}{l.phone}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn primary" style={{ flex: 1 }} onClick={() => void dialBatch()} disabled={batchDialing || sipState === 'on-call'}>
                <Icon name="bolt" size={14} /> {batchDialing ? 'מחייג…' : 'סבב חיוג נוסף'}
              </button>
              <button type="button" className="btn ghost" onClick={() => void switchToManual()}>חיוג ידני (ליד-ליד)</button>
            </div>
          </div>
        </Card>
      ) : exhausted ? (
        <EmptyState icon="ok" title="סיימת את כל הלידים בקמפיין" description="אין כרגע לידים נוספים לחיוג." />
      ) : fetchError && !lead ? (
        <Card>
          <EmptyState
            icon="warn"
            title="שגיאה בטעינת הליד"
            description="לא ניתן היה לטעון את הליד הבא מהשרת."
            action={<button type="button" className="btn primary" onClick={() => activeCampaign && void fetchNext(activeCampaign)}>נסה שוב</button>}
          />
        </Card>
      ) : !lead ? (
        <Card>טוען ליד…</Card>
      ) : phase === 'lead' ? (
        <Card title="הליד הבא">
          <div className="stack">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="target" size={24} color="var(--accent)" />
              <div>
                <PhoneCell number={lead.phone} />
                {lead.name && <div style={{ marginTop: 4 }}>{lead.name}</div>}
              </div>
            </div>
            {lead.fields && Object.keys(lead.fields).length > 0 && (
              <div style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)' }}>
                {Object.entries(lead.fields).map(([k, v]) => (
                  <div key={k}><strong>{k}:</strong> {String(v)}</div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn primary" style={{ flex: 1 }} onClick={dialLead}>
                <Icon name="phone" size={14} /> חייג
              </button>
              <button type="button" className="btn" onClick={skip}>דלג</button>
            </div>
          </div>
        </Card>
      ) : (
        <Card title="סיכום שיחה">
          <div className="stack">
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              <PhoneCell number={lead.phone} /> {lead.name && `· ${lead.name}`}
            </div>
            {activeCampaign.quick_actions?.length ? (
              <QuickActionsRow
                actions={activeCampaign.quick_actions}
                campaignId={activeCampaign.id}
                context={{ phone: lead.phone, name: lead.name, lead_id: lead.id }}
              />
            ) : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {dispositions.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  className={`btn ${chosen === d.code ? 'primary' : ''}`}
                  onClick={() => setChosen(d.code)}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {dispositions.find((d) => d.code === chosen)?.requires_followup && (
              <div>
                <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginBottom: 4 }}>מועד חזרה</div>
                <input
                  type="datetime-local"
                  value={callbackAt}
                  onChange={(e) => setCallbackAt(e.target.value)}
                  style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
                />
              </div>
            )}
            {products.length > 0 && isDealCode(dispositions.find((d) => d.code === chosen), chosen) && (
              <div>
                <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginBottom: 4 }}>מוצר</div>
                <select
                  className="input"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  aria-label="מוצר"
                  style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
                >
                  <option value="">ללא מוצר</option>
                  {products.map((p) => (
                    <option key={p.id} value={String(p.id)}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
            {(() => {
              const def = dispositions.find((d) => d.code === chosen);
              const noteRequired = !!def?.requires_note;
              const noteMissing = noteRequired && !notes.trim();
              return (
                <>
                  {chosen && (
                    <div>
                      <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginBottom: 4 }}>
                        הערה{noteRequired && <span style={{ color: 'var(--danger)' }}> *</span>}
                      </div>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        placeholder="סיכום השיחה…"
                        style={{ width: '100%', padding: 10, border: `1px solid ${noteMissing ? 'var(--danger)' : 'var(--line)'}`, borderRadius: 'var(--r-2)', background: 'var(--surface)', fontFamily: 'inherit', fontSize: 'var(--t-sm)', resize: 'vertical' }}
                      />
                      {noteMissing && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)', marginTop: 2 }}>סטטוס זה מחייב הערה</div>}
                    </div>
                  )}
                  {activeCampaign.auto_dial_next === false ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {!disposed ? (
                        <button type="button" className="btn primary" style={{ flex: 1 }} onClick={saveDisposition} disabled={!chosen || noteMissing} title={noteMissing ? 'סטטוס זה מחייב הערה' : undefined}>
                          שמור תיעוד
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn primary"
                          style={{ flex: 1 }}
                          onClick={() => fetchNext(activeCampaign)}
                          disabled={activeCampaign.wrapup_required && activeCampaign.wrapup_blocks_next && !disposed}
                        >
                          לליד הבא
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="btn primary" style={{ flex: 1 }} onClick={saveDisposition} disabled={!chosen || noteMissing} title={noteMissing ? 'סטטוס זה מחייב הערה' : undefined}>
                        שמור והמשך לליד הבא
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </Card>
      )}
    </div>
  );
}
